/**
 * Direct-to-Gemini mode.
 *
 * When `GEMINI_API_KEY` is set, the SDK routes `google/*` model calls straight
 * to Google's Generative AI API (OpenAI-compatible endpoint) instead of going
 * through the Anvil backend or falling back to DeepSeek.
 *
 * Google's OpenAI-compatible endpoint:
 *   https://generativelanguage.googleapis.com/v1beta/openai/
 *
 * Latest models (source: https://ai.google.dev/gemini-api/docs/models):
 *   google/gemini-3.7-flash       → gemini-3.7-flash       (latest, most capable)
 *   google/gemini-3.6-flash       → gemini-3.6-flash
 *   google/gemini-3.5-flash       → gemini-3.5-flash
 *   google/gemini-3.5-flash-lite  → gemini-3.5-flash-lite
 *   google/gemini-3.1-pro-preview → gemini-3.1-pro-preview  (Pro model)
 *   google/gemini-3.1-flash-lite  → gemini-3.1-flash-lite
 *   google/gemini-3-flash-preview → gemini-3-flash-preview
 *   google/gemini-2.5-pro         → gemini-2.5-pro
 *   google/gemini-2.5-flash       → gemini-2.5-flash
 *   google/gemini-2.5-flash-lite  → gemini-2.5-flash-lite
 */

export const GEMINI_BASE_URL =
  'https://generativelanguage.googleapis.com/v1beta/openai'

/** Model names Google's API actually accepts. */
export const GEMINI_MODEL_MAP: Record<string, string> = {
  // Gemini 3.7 (latest)
  'google/gemini-3.7-flash': 'gemini-3.7-flash',
  // Gemini 3.6
  'google/gemini-3.6-flash': 'gemini-3.6-flash',
  // Gemini 3.5
  'google/gemini-3.5-flash': 'gemini-3.5-flash',
  'google/gemini-3.5-flash-lite': 'gemini-3.5-flash-lite',
  // Gemini 3.1
  'google/gemini-3.1-pro-preview': 'gemini-3.1-pro-preview',
  'google/gemini-3.1-flash-lite': 'gemini-3.1-flash-lite',
  // Gemini 3
  'google/gemini-3-flash-preview': 'gemini-3-flash-preview',
  // Gemini 2.5
  'google/gemini-2.5-pro': 'gemini-2.5-pro',
  'google/gemini-2.5-flash': 'gemini-2.5-flash',
  'google/gemini-2.5-flash-lite': 'gemini-2.5-flash-lite',
}

/** Fallback when the model ID isn't in the map. */
export const DEFAULT_DIRECT_GEMINI_MODEL = 'gemini-3.7-flash'

/** The user's Gemini API key, or undefined when direct Gemini mode is off. */
export const getDirectGeminiApiKey = (): string | undefined =>
  process.env.GEMINI_API_KEY

/** True when the SDK should route google/* models to Google directly. */
export const isDirectGeminiMode = (): boolean =>
  Boolean(getDirectGeminiApiKey())

/** True when a model ID belongs to the Google/Gemini family. */
export function isGeminiModelId(model: string): boolean {
  return model.startsWith('google/gemini')
}

/** Map a codebuff model ID to the model name Google's API accepts. */
export function remapModelForDirectGemini(model: string): string {
  return GEMINI_MODEL_MAP[model] ?? DEFAULT_DIRECT_GEMINI_MODEL
}
